import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';

/** Fallback loaders by filter key when filterDef.optionsLoader is unset. */
const OPTION_LOADERS = {
  aircraftId: 'aircraft',
  crewId: 'crew',
  supplierId: 'suppliers',
  workOrderId: 'work-orders',
  submittedBy: 'users',
  issuedTo: 'users-with-external'
};

function resolveOptionsLoader(filterDef) {
  if (filterDef?.optionsLoader) return filterDef.optionsLoader;
  return OPTION_LOADERS[filterDef?.key] || null;
}

function defaultFilters(section) {
  const defaults = {};
  (section?.filters || []).forEach((f) => {
    if (f.type === 'boolean') defaults[f.key] = false;
    else if (f.defaultValue !== undefined) defaults[f.key] = f.defaultValue;
    // Leave optional selects/text/dates empty — "no filter" is valid.
    else defaults[f.key] = '';
  });
  if (section?.defaultSort) {
    defaults.sortBy = section.defaultSort.by;
    defaults.sortOrder = section.defaultSort.order;
  }
  return defaults;
}

const ReportSectionFiltersDialog = ({
  open,
  section,
  initialFilters,
  onClose,
  onConfirm,
  confirmLabel = 'Add section'
}) => {
  const [filters, setFilters] = useState({});
  const [options, setOptions] = useState({});

  const hasFilters = (section?.filters || []).length > 0;
  const hasSortOptions = (section?.sortOptions || []).length > 0;
  const sortOptions = section?.sortOptions || [];

  useEffect(() => {
    if (!open || !section) return;
    setFilters({ ...defaultFilters(section), ...(initialFilters || {}) });
  }, [open, section, initialFilters]);

  useEffect(() => {
    if (!open || !section) return;

    const loaders = new Set();
    (section.filters || []).forEach((f) => {
      const loader = resolveOptionsLoader(f);
      if (loader) loaders.add(loader);
    });

    loaders.forEach(async (type) => {
      try {
        const res = await axios.get(`/api/report-center/filter-options/${type}`);
        setOptions((prev) => ({ ...prev, [type]: res.data.options || [] }));
      } catch {
        setOptions((prev) => ({ ...prev, [type]: [] }));
      }
    });
  }, [open, section]);

  const requiredMissing = useMemo(() => {
    if (!section) return [];
    return (section.filters || [])
      .filter((f) => f.required && !filters[f.key])
      .map((f) => f.label);
  }, [section, filters]);

  const handleChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleConfirm = () => {
    onConfirm({ filters });
  };

  const renderField = (filterDef) => {
    const { key, label, type } = filterDef;
    const value = filters[key];

    if (type === 'boolean') {
      return (
        <FormControlLabel
          key={key}
          control={
            <Checkbox
              checked={Boolean(value)}
              onChange={(e) => handleChange(key, e.target.checked)}
            />
          }
          label={label}
        />
      );
    }

    if (type === 'select') {
      const loader = resolveOptionsLoader(filterDef);
      const selectOptions = filterDef.options?.length
        ? filterDef.options
        : (loader ? (options[loader] || []) : []);
      const hasBlankOption = selectOptions.some((opt) => opt.value === '');
      return (
        <FormControl key={key} fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            value={value ?? ''}
            label={label}
            onChange={(e) => handleChange(key, e.target.value)}
          >
            {!hasBlankOption && !filterDef.required && (
              <MenuItem value="">
                <em>All / no filter</em>
              </MenuItem>
            )}
            {selectOptions.map((opt) => (
              <MenuItem key={String(opt.value)} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (type === 'date' || type === 'dateRange') {
      return (
        <TextField
          key={key}
          label={label}
          type="date"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={value || ''}
          onChange={(e) => handleChange(key, e.target.value)}
        />
      );
    }

    return (
      <TextField
        key={key}
        label={label}
        size="small"
        fullWidth
        multiline={Boolean(filterDef.multiline)}
        minRows={filterDef.multiline ? 4 : undefined}
        value={value || ''}
        onChange={(e) => handleChange(key, e.target.value)}
      />
    );
  };

  if (!section) return null;

  const hasConfiguration = hasFilters || hasSortOptions;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configure section — {section.name}</DialogTitle>
      <DialogContent dividers>
        {!hasConfiguration && (
          <Typography variant="body2" color="text.secondary">
            This section has no filters or sort options to configure.
          </Typography>
        )}
        {hasFilters && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              {section.sectionType === 'data-table' ? 'Row filters' : 'Section settings'}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {section.sectionType === 'data-table'
                ? 'Filter which records appear in this table. Filters are optional — leave blank or choose All to include all rows.'
                : 'Configure this section before adding it to the report.'}
            </Typography>
            <Stack spacing={2}>
              {(section.filters || []).map(renderField)}
            </Stack>
            {requiredMissing.length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="warning.main">
                  Required: {requiredMissing.join(', ')}
                </Typography>
              </Box>
            )}
          </>
        )}

        {hasFilters && hasSortOptions && <Divider sx={{ my: 2 }} />}

        {hasSortOptions && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Sort order
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Match the sort options from the source screen.
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={filters.sortBy || section.defaultSort?.by || 'dueDate'}
                  label="Sort by"
                  onChange={(e) => handleChange('sortBy', e.target.value)}
                >
                  {sortOptions.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Direction</InputLabel>
                <Select
                  value={filters.sortOrder || section.defaultSort?.order || 'asc'}
                  label="Direction"
                  onChange={(e) => handleChange('sortOrder', e.target.value)}
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={requiredMissing.length > 0}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportSectionFiltersDialog;
